import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Select,
  Tabs,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import {
  IconContrast,
  IconEdit,
  IconFunction,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import { _localization } from "../../../config/location";
import { UserMenuGroup } from "../../../model/UserMenuGroup";
import { useLocation } from "react-router-dom";
import { repositoryPos } from "../../../_base/_const/_constVar";
import { MessageResponse } from "../../../model/MessageResponse";
import { modals } from "@mantine/modals";
import DecentralizationReport from "./DecentralizationReport";
import ReportByEmployee from "./ReportByEmployee";

const ReportCategory = () => {
  //#region  table
  const headerRef = React.useRef<HTMLDivElement>(null);
  //data and fetching state
  const [data, setData] = useState<UserMenuGroup[]>([]);

  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  //table state
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [height, setHeight] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  //
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [keySearch, setKeySearch] = useDebouncedState(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300
  );
  const [search, setSearch] = useState<any>({});
  const [selectIds, setSelectIds] = useState<string[]>([]);

  const [deleteViewStatus, setDeleteViewStatus] = useState(0);

  // Mac dinh mo cach xem theo nhan vien vi day la cau hoi hay gap nhat:
  // "nguoi nay dang xem duoc nhung bao cao nao"
  const [activeTab, setActiveTab] = useState<string | null>("by-employee");

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "code",
        header: "Mã báo cáo",
      },
      {
        accessorKey: "name",
        header: "Tên báo cáo",
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        size: 175,
        enableEditing: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Tooltip label="Phân quyền">
              <ActionIcon
                variant="light"
                aria-label="Settings"
                color="teal"
                onClick={() =>
                  modalsDecentralizationReport(
                    row.original.code,
                    row.original.name
                  )
                }
              >
                <IconFunction size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  const modalsDecentralizationReport = (code: string, name: string) => {
    modals.openConfirmModal({
      title: <Title order={5}>Phân quyền báo cáo</Title>,
      size: "auto",
      children: <DecentralizationReport code={code} name={name} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/AuthReportReading/get-list-auth-list-report?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (
          value &&
          ((typeof value === "string" && value.length > 0) ||
            (typeof value === "number" && value > 0) ||
            (Array.isArray(value) && value.length > 0))
        ) {
          params.append(key, String(value));
        }
      });

      if (keySearch?.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const res = await repositoryPos.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      localStorage.setItem(
        String(location.pathname),
        JSON.stringify({ ...search, keySearch: keySearch })
      );
    }
  };

  useEffect(() => {
    const valuesList = Object.keys(rowSelection);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelection);
      setSelectIds(valuesList);
    }
  }, [rowSelection]);

  useEffect(() => {
    fetchData();
  }, [deleteViewStatus, sorting, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    const handleResize = () => {
      // Đo ngay lúc đổi kích thước chứ không đo một lần rồi giữ mãi,
      // vì phần tìm kiếm xuống dòng khi màn hình hẹp thì chiều cao đổi theo.
      const headerHeight = headerRef.current?.offsetHeight || 0;
      // 280 gồm phần phân trang, đường dẫn điều hướng và thanh chọn tab
      setHeight(window.innerHeight - (280 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize); // Update height on window resize

    return () => {
      window.removeEventListener("resize", handleResize); // Clean up event listener
    };
    // Đổi tab thì phần tìm kiếm mới được dựng lại, phải đo lại mới ra đúng chiều cao
  }, [activeTab]);

  const table = useMantineReactTable({
    columns,
    data,
    positionToolbarAlertBanner: "bottom",
    enableTopToolbar: false,
    enableRowNumbers: true,
    renderRowActions: () => <></>,
    //add custom action buttons to top-left of top toolbar

    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: false,
    getRowId: (row) => row?.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-select"],
        // right: ["action"],
      },
      columnVisibility: { id: false },
      density: "xs",
      pagination: {
        pageIndex: 0,
        pageSize: 50,
      },
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    rowCount,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        //  fontWeight: row.getIsSelected() ? "bold" : "normal", //conditional styling
        fontWeight: "bold", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
        //  backgroundColor: row.original.userName.length>0 ? "white" : "gray",
      },
    }),
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      sorting,
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["50", "100", "150"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
  });
  //#endregion
  return (
    <>
      <div className="mt-5">
        <Flex
          justify={"space-between"}
          wrap={"wrap"}
          align={"center"}
          w={"100%"}
        >
          <_breadcrumb></_breadcrumb>
        </Flex>

        {/*
          Cùng một dữ liệu phân quyền nhưng nhìn từ hai phía:
          theo nhân viên để biết một người xem được gì,
          theo báo cáo để biết một báo cáo đang cấp cho ai.
        */}
        <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="by-employee">Theo nhân viên</Tabs.Tab>
            <Tabs.Tab value="by-report">Theo báo cáo</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="by-employee" pt="xs">
            <ReportByEmployee />
          </Tabs.Panel>

          <Tabs.Panel value="by-report" pt="xs">
            <div ref={headerRef} style={{ width: "100%" }}>
              <Grid>
                <Grid.Col span={{ base: 12, xs: 6, md: 6, lg: 2 }}>
                  <TextInput
                    placeholder="Nhập từ khóa ..."
                    defaultValue={keySearch}
                    onChange={(e) => {
                      setKeySearch(e.currentTarget.value ?? "");
                    }}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, xs: 6, md: 6, lg: 2 }}>
                  <Button
                    leftSection={<IconSearch size={14} />}
                    color="blue"
                    variant="outline"
                    onClick={async () => {
                      await fetchData();
                    }}
                  >
                    Tìm kiếm
                  </Button>
                </Grid.Col>
              </Grid>
            </div>
            <Box mt={10}>
              <MantineReactTable table={table} />
            </Box>
          </Tabs.Panel>
        </Tabs>
      </div>
    </>
  );
};

export default ReportCategory;
